import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { FaChalkboardTeacher, FaCreditCard, FaHandshake, FaPagelines } from 'react-icons/fa';

import { ContainerFluid } from '@/components/container/Container';
import { BodySm, H2, H5 } from '@/components/ui/typography/Typography';
import { useHomeQuery } from '@/queries/home.query';

/* ========================
 * Icon Mapper
 * ======================== */
const ICON_MAP = {
  simpanan: FaCreditCard,
  pembiayaan: FaHandshake,
  ziswaf: FaPagelines,
  pelatihan: FaChalkboardTeacher,
};

/* ========================
 * Skeleton
 * ======================== */
// const ServicesSkeleton = () => (
//   <section className="py-20 bg-primary-50">
//     <ContainerFluid>
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
//         <div className="space-y-5">
//           {[...Array(4)].map((_, i) => (
//             <div key={i} className="h-24 bg-white rounded-xl" />
//           ))}
//         </div>
//         <div className="h-[420px] bg-white rounded-xl" />
//       </div>
//     </ContainerFluid>
//   </section>
// );

export default function ServicesSection() {
  const { data, isLoading, isError } = useHomeQuery();
  const [selected, setSelected] = useState(0);

  /* ========================
   * Mapping Services
   * ======================== */
  const services = useMemo(() => {
    return (
      data?.data?.services
        ?.filter((item) => item.is_active)
        .sort((a, b) => a.position - b.position)
        .map((item) => ({
          title: item.title,
          desc: item.description,
          image: item.hero_image,
          link: `/produk-layanan/${item.slug}`,
          icon: ICON_MAP[item.slug] ?? FaHandshake,
        })) ?? []
    );
  }, [data]);

  // if (isLoading) return <ServicesSkeleton />;
  if (isLoading) return null;
  if (isError || !services.length) return null;

  return (
    <section className="py-10 sm:py-20 bg-primary-50">
      <ContainerFluid>
        {/* TITLE */}
        <div className="w-full max-w-screen-xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="text-xs sm:text-sm font-medium tracking-[0.15em] uppercase text-gray-500"
          >
            Produk & Layanan
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-800 mt-2"
          >
            <span className="text-primary">Membantu Anggota </span>
            Mengembangkan Kesejahteraan
          </motion.h2>
        </div>

        {/* CONTENT */}
        <div className="mx-auto py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* LEFT */}
          <div className="flex flex-col justify-center space-y-5 order-2 lg:order-1">
            {services.map((item, index) => {
              const Icon = item.icon;

              return (
                <motion.button
                  key={index}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.1 }}
                  onClick={() => setSelected(index)}
                  className={`flex text-left items-start gap-4 px-6 py-4 rounded-xl border transition-all
                  ${
                    selected === index
                      ? 'bg-white shadow-lg border-primary/55'
                      : 'bg-white/70 backdrop-blur-md border-primary/10 hover:shadow-md hover:bg-white'
                  }`}
                >
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <H2>
                      <Icon className="text-primary" />
                    </H2>
                  </div>

                  <div className="flex-1">
                    <H5 className="pt-2">{item.title}</H5>
                    <BodySm className="text-gray-600 py-1 line-clamp-1">{item.desc}</BodySm>

                    {selected === index && (
                      <motion.a
                        href={item.link}
                        initial={{ opacity: 0, x: 5 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.25 }}
                        className="text-primary text-xs sm:text-sm mt-2 flex justify-end font-medium hover:underline"
                      >
                        Lihat selengkapnya →
                      </motion.a>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* RIGHT IMAGE */}
          <div className="relative w-full h-[420px] lg:h-[480px] rounded-xl overflow-hidden order-1 lg:order-2">
            <AnimatePresence mode="wait">
              <motion.img
                key={selected}
                src={services[selected].image}
                alt={services[selected].title}
                className="absolute inset-0 w-full h-full object-contain"
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.5 }}
                loading={selected === 0 ? 'eager' : 'lazy'}
                decoding="async"
                fetchpriority={selected === 0 ? 'high' : 'low'}
              />
            </AnimatePresence>
          </div>
        </div>
      </ContainerFluid>
    </section>
  );
}
